import * as React from "react";
import { Button, Text, makeStyles } from "@fluentui/react-components";
import type {
  ConditionDraft,
  ConditionGroupDraft,
  QueryEntityMetadata,
  QueryTarget,
} from "../../types.js";
import { ConditionGroupTree } from "./condition-editor/ConditionGroupTree.js";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: "8px",
  },
  row: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
});

export interface TargetFiltersEditorControlProps {
  target: QueryTarget;
  title?: string;
  conditionGroups: ConditionGroupDraft[];
  conditions: ConditionDraft[];
  rootMetadata: QueryEntityMetadata | null;
  joinMetadataByJoinId: Record<string, QueryEntityMetadata | undefined>;
  onAddTopLevelGroup: (target: QueryTarget) => void;
  onAddCondition: (groupId: string) => void;
  onAddSubGroup: (groupId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onChangeGroup: (groupId: string, update: Partial<ConditionGroupDraft>) => void;
  onChangeCondition: (conditionId: string, update: Partial<ConditionDraft>) => void;
  onRemoveCondition: (conditionId: string) => void;
}

export function TargetFiltersEditorControl({
  target,
  title = "Filters",
  conditionGroups,
  conditions,
  rootMetadata,
  joinMetadataByJoinId,
  onAddTopLevelGroup,
  onAddCondition,
  onAddSubGroup,
  onRemoveGroup,
  onChangeGroup,
  onChangeCondition,
  onRemoveCondition,
}: TargetFiltersEditorControlProps): JSX.Element {
  const styles = useStyles();
  const topGroups = conditionGroups.filter((group) => group.target === target && group.parentGroupId === null);

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <Text weight="semibold">{title}</Text>
        <Button appearance="secondary" onClick={() => onAddTopLevelGroup(target)}>
          Add group
        </Button>
      </div>

      {topGroups.length === 0 ? <Text>No filters configured for this relation.</Text> : null}

      {topGroups.map((group) => (
        <ConditionGroupTree
          key={group.id}
          group={group}
          depth={0}
          conditionGroups={conditionGroups}
          conditions={conditions}
          rootMetadata={rootMetadata}
          joinMetadataByJoinId={joinMetadataByJoinId}
          onAddCondition={onAddCondition}
          onAddSubGroup={onAddSubGroup}
          onRemoveGroup={onRemoveGroup}
          onChangeGroup={onChangeGroup}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
        />
      ))}
    </div>
  );
}